import { Alert, LinearProgress, TextField } from '@mui/material';
import type { UseQueryResult } from '@tanstack/react-query';

import type { AffiliateCampaignDetails, SelectedWelcomeBonus } from '../../../../@types/api';
import { WelcomeBonusReview } from '../welcome-bonus/WelcomeBonusReview';

export type CampaignBonusDetailsState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; bonus: SelectedWelcomeBonus | null };

export const getCampaignBonusDetailsState = (
  query: Pick<UseQueryResult<AffiliateCampaignDetails | null>, 'data' | 'isError' | 'isLoading'>
): CampaignBonusDetailsState => {
  if (query.isLoading) return { status: 'loading' };
  if (query.isError || !query.data) return { status: 'error' };

  return { status: 'ready', bonus: query.data.selectedWelcomeBonus };
};

const SelectedWelcomeBonusDetails = ({ bonus }: { bonus: SelectedWelcomeBonus }) => (
  <>
    <TextField label='Bonus' value={bonus.name ?? bonus.id} disabled fullWidth />
    {!bonus.sourceAvailable && (
      <Alert severity='warning'>The source bonus is unavailable. Showing the saved campaign terms.</Alert>
    )}
    {!bonus.campaignBonus && <Alert severity='warning'>The saved bonus configuration is unavailable.</Alert>}
    <WelcomeBonusReview campaign={bonus} />
  </>
);

const CampaignBonusDetails = ({ state }: { state: CampaignBonusDetailsState }) => {
  if (state.status === 'loading') {
    return <LinearProgress aria-label='Loading linked bonus details' />;
  }

  if (state.status === 'error') {
    return <Alert severity='warning'>Unable to load linked bonus details. Campaign fields can still be edited.</Alert>;
  }

  if (state.bonus === null) {
    return <Alert severity='warning'>No bonus is linked to this campaign.</Alert>;
  }

  return <SelectedWelcomeBonusDetails bonus={state.bonus} />;
};

export default CampaignBonusDetails;
